// Owner Pre-Authorize flags (CHEKT-2084): raw API keys (int 0/1) → camelCase
// booleans. Reused by parseSiteData and by the PUT-response commit.
export function parsePreAuthorize (data) {
  const d = data || {}
  // GET returns these as STRINGS ("0"/"1"); PUT returns ints (0/1). Compare
  // numerically — `!!"0"` is true, so a plain boolean cast would misread "0".
  const on = (v) => Number(v) === 1
  return {
    executeBypass: on(d.pre_authorize_execute_bypass),
    clearBypass: on(d.pre_authorize_clear_bypass),
    tempDisarm: on(d.pre_authorize_temp_disarm),
    arm: on(d.pre_authorize_arm),
    disarm: on(d.pre_authorize_disarm),
  }
}

export default {
  parseSiteData: function (data) {
    // arming info
    let armingType, armingLabel;
    let devicesArmingInfo = data?.arming_state?.devices || [];
    // Check if the array includes both "armed" and "disarmed"
    const hasArmed = devicesArmingInfo.some(item => item?.arming_state === "armed" && item?.is_online && item?.is_registered && '' + item?.registered_by_partner?.toLowerCase() !== "adc");
    const hasDisarmed = devicesArmingInfo.some(item => item?.arming_state === "disarmed" && item?.is_online && item?.is_registered && '' + item?.registered_by_partner?.toLowerCase() !== "adc");
    if (hasArmed && hasDisarmed) {
      armingType = 3; // Mixed
      armingLabel = "partially armed"
    } 
    else if (hasArmed) {
      armingType = 1; // All armed
      armingLabel = "armed"
    } 
    else if (hasDisarmed) {
      armingType = 2; // All disarmed
      armingLabel = "disarmed"
    } 
    else {
      armingType = 0; // Invalid input or no relevant states
      armingLabel = "unknown"
    }
        
    return {
      siteId: data.site_id,
      siteName: data.name,
      armingSource: data.arming_source, // 1: Master Bridge Arming || 2: Individual Bridge Arming || 3: Site Based Arming || 4: ADC Based Arming
      siteMap: {
        blueprintUrl: data.blueprint_url,
        defaultFloor: data.default_floor,
        floors: data.floors ? JSON.parse(data.floors) : null,
      },
      locationInfo: {
        address: (data.address2 ? data.address2 + ' ' : '') + data.address1,
        timezone: data.timezone ? data.timezone : 'UTC',
        timezoneOffset: data.timezone_offset,
        lat: data.lat,
        lng: data.lng
      },
      dealerInfo: {
        dealerId: data.dealer_id,
      },
      verificationInfo: {
        verificationDuration: data.verification_duration,
      },
      // Approval config (CHEKT-2084): DP-set MP action modes + owner pre-authorize
      // flags. `mp[action].mode` is 'with_approval' | 'always' | … ; a pre-authorize
      // toggle only has effect when its action's mode is 'with_approval'.
      approvalConfig: {
        mp: {
          bypass: { enabled: Number(data.mp_execute_bypass_enabled) === 1, mode: data.mp_execute_bypass_mode || null },
          clearBypass: { enabled: Number(data.mp_clear_bypass_enabled) === 1, mode: data.mp_clear_bypass_mode || null },
          tempDisarm: { enabled: Number(data.mp_temp_disarm_enabled) === 1, mode: data.mp_temp_disarm_mode || null },
          arm: { enabled: Number(data.mp_arm_enabled) === 1, mode: data.mp_arm_mode || null },
          disarm: { enabled: Number(data.mp_disarm_enabled) === 1, mode: data.mp_disarm_mode || null },
        },
        preAuthorize: parsePreAuthorize(data),
      },
      accountInfo: {
        prefix: data.prefix, // account number package
        prefixAccountNumber: data.prefix_account_number, // account number package
        customerAccountNumber: data.customer_account_number, // account number package
        receiverLinePrefix: data.receiver_line_prefix, // account number package
        partitionNumber: data.partition_number,
        virtualZone: data.virtual_zone,
      },
      statusInfo: {
        isDeleted: !!data.is_deleted,
        isMonitoringBlocked: !!data.is_monitoring_block,
        createdDate: data.created ? new Date(data.created) : null,
        deletedDate: data.deleted_time ? new Date(data.deleted_time) : null,
        showArmingNotification: !!data.show_arming_config_enduser_app,
        showEventNotification: !!data.show_event_config_enduser_app,
        showBypassNotification: !!data.show_bypass_config_enduser_app,
        showDeviceConnectionNotification: !!data.show_device_connection_config_enduser_app,
        showIntercomNotification: !!data?.show_intercom_config_enduser_app,
        showDisarmedActivityNotification: !!data?.show_disarmed_event_config_enduser_app,
        showCameraRelays: !!data.show_camera_relays_config_enduser_app,
        showTalkdown: !!data.show_talkdown_config_enduser_app,
        showSpeak: !!data.show_speak_config_enduser_app,
        showListen: !!data.show_listen_config_enduser_app,
        showPtz: !!data.show_ptz_config_enduser_app,
        showCameraModel: !!data?.show_camera_model_enduser_app,
        showMemberPtz: !!data.show_member_ptz_config_enduser_app,
        showVideo: !!data.show_stream_config_enduser_app,
        armingScheduleEnabled: !!data?.arming_schedule_enabled,
        allowDisarmedEventControlConfigEnduserApp:!!data?.allow_disarmed_event_control_config_enduser_app,
        hasVideoTimeoutInWeb: data?.has_video_timeout_in_web,
        limitArmingToArmedTime: !!data?.limit_arming_to_armed_time_config_enduser_app,
        allowModifyArmingSchedule: !!data?.allow_modify_arming_schedule_config_enduser_app,
        guardTourScheduleEnabled: !!data?.guard_tour_schedule_enabled,
        guardTourScheduleTimelapseEnabled: !!data?.guard_tour_schedule_timelapse_enabled,
        activityLogVersion: data?.activity_log_version === 2
      },
      planInfo: {
        eventPlanCount: data.event_plan_count,
        isEventEnabled: data.is_event_enabled,
        isLiveEnabled: data.is_live_enabled,
        isPlaybackEnabled: data.is_playback_enabled,
        isVpnEnabled: data.is_vpn_enabled,
        currServicePlanType: data.service_plan_info?.curr_service_plan_type,
        nextServicePlanType: data.service_plan_info?.next_service_plan_type,
        videoStorageRetentionPeriod: data.service_plan_info?.video_storage_retention_period,
        sitePlanInfo: data?.site_plan_info || {},
      },
      statistics: {
        totalDevice: data.device_count ? data.device_count : 0,
        totalBridge: data.bridge_count ? data.bridge_count : 0,
        totalCamera: data.camera_count ? data.camera_count : 0,
        totalUser: data.user_count ? data.user_count : 0
      },
      armingInfo: {
        armingType: armingType,
        armingLabel: armingLabel,
      },
      isPartitionEnabled: !!data?.is_partition_enabled,
      expSiteInfo: data.external_partner_sites || [],
    }
  },
}
